import React from 'react';
import { ArrowUpRight, ArrowRight, ChevronDown } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { IMAGES } from '../mock/mock';

const Hero = () => {
  const { t, lang } = useLang();
  const rtl = lang === 'ar';
  return (
    <section id="home" className="relative min-h-screen pt-52 pb-16 overflow-hidden ambient-glow">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-3 items-center font-mono text-[10px] tracking-[0.35em] text-cream/60 pt-4">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot"></span>
          <span>{t.hero.live}</span>
        </div>
        <div className="text-center">N 24°28′ · E 54°22′</div>
        <div className={rtl ? 'text-left' : 'text-right'}>{t.hero.est}</div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mt-14 lg:mt-24">
        <div>
          <div className={`inline-block font-mono text-[10px] tracking-[0.35em] text-[#c99a3d] mb-8 py-1 ${rtl ? 'border-r-2 pr-3' : 'border-l-2 pl-3'} border-[#c99a3d]/60`}>
            {t.hero.manifesto}
          </div>

          <h1 className="font-serif font-light text-cream leading-[0.95] tracking-tight text-[64px] md:text-[88px] xl:text-[104px]">
            {t.hero.titleA}<br />
            <span className="text-[#c99a3d] italic font-normal">{t.hero.titleB}</span><br />
            {t.hero.titleC}
          </h1>

          <p className="mt-10 max-w-[560px] text-cream/75 text-[17px] leading-relaxed">
            {t.hero.body}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#technology"
              className="group inline-flex items-center gap-3 bg-[#ebe6d5] text-[#0a1220] rounded-full px-7 py-4 font-mono text-[11px] tracking-[0.28em] hover:bg-white transition-colors"
            >
              {t.hero.ctaA}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.6} />
            </a>
            <a
              href="#contact"
              className="group inline-flex items-center gap-3 border border-cream/30 text-cream rounded-full px-7 py-4 font-mono text-[11px] tracking-[0.28em] hover:border-cream/60 hover:bg-cream/5 transition-all"
            >
              {t.hero.ctaB}
              <ArrowRight className={`w-4 h-4 transition-transform group-hover:${rtl ? '-translate-x-0.5' : 'translate-x-0.5'} ${rtl ? 'rotate-180' : ''}`} strokeWidth={1.6} />
            </a>
          </div>
        </div>

        <div className="relative aspect-[4/5] max-h-[720px] w-full">
          <div className="absolute inset-0 corner-brackets">
            <img src={IMAGES.hero} alt="Autonomous drone over Dubai skyline" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220]/50 via-transparent to-transparent"></div>
            <div className={`absolute top-6 ${rtl ? 'right-6' : 'left-6'} font-mono text-[10px] tracking-[0.3em] text-cream/85 flex items-center gap-2`}>
              <span className="w-1.5 h-1.5 bg-[#c99a3d] rounded-full"></span> {t.hero.unit}
            </div>
            <div className={`absolute bottom-6 ${rtl ? 'left-6 text-left' : 'right-6 text-right'} font-mono text-[10px] tracking-[0.3em] text-cream/85`}>
              {t.hero.payload}<br />
              <span className="text-cream text-[13px] font-serif tracking-normal">6.0 kg</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 mt-24">
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-cream/10">
          {t.hero.metrics.map((m, i) => (
            <div key={i} className={`py-8 px-6 ${i > 0 ? 'md:border-l border-cream/10' : ''} ${i > 1 ? 'border-t md:border-t-0' : ''}`}>
              <div className="font-serif text-4xl md:text-5xl text-cream font-light">{m.value}</div>
              <div className="mt-3 font-mono text-[10px] tracking-[0.25em] text-cream/55 uppercase">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 flex flex-col items-center gap-2 text-cream/45">
        <span className="font-mono text-[9px] tracking-[0.35em]">{t.hero.scroll}</span>
        <ChevronDown className="w-3 h-3 animate-bounce" strokeWidth={1.5} />
      </div>
    </section>
  );
};

export default Hero;
